import { DeleteModal, Dropdown, LoadingSpinner, ToastHelper } from '@cfs-fixings/shared'
import { ProjectResource, useDeleteProjectsByProjectMutation } from '@/api/serverApi'
import ProjectListView from './views/ProjectListView'
import ProjectGridView from './views/ProjectGridView'
import { useState } from 'react'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import UpdateProjectModal from '@/components/modals/projects/UpdateProjectModal'
import DuplicateProjectModal from '@/components/modals/projects/DuplicateProjectModal'
import { useNavigate } from 'react-router-dom'

type PropTypes = {
  projects?: ProjectResource[]
  footerElements?: React.ReactNode[]
  viewType?: 'list' | 'grid'
  isLoading?: boolean
}

type ProjectDropdownProps = {
  project: ProjectResource
}

const textSpec = {
  deleteModal: {
    successMessage: {
      title: 'Project deleted successfully',
      message: 'Project has been removed from the system',
    },
    errorMessage: {
      title: 'Failed to delete project',
      message: 'Project cannot be deleted at this time, please try again later',
    },
    modal: {
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project?',
    },
    button: {
      text: 'Delete',
    },
  },
  updateModal: {
    button: {
      text: 'Edit',
    },
  },
  duplicateModal: {
    button: {
      text: 'Duplicate',
    },
  },
}

const ProjectDropdown = ({ project }: ProjectDropdownProps) => {
  const navigate = useNavigate()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteMutation, { isLoading }] = useDeleteProjectsByProjectMutation()

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)

  const onDelete = () => {
    deleteMutation({ body: {}, project: project.id }).unwrap().then(() => {
      setIsDeleteModalOpen(false)
      toastHelper.success(
        textSpec.deleteModal.successMessage.title,
        textSpec.deleteModal.successMessage.message,
      )

    }).catch(() => {
      toastHelper.error(
        textSpec.deleteModal.errorMessage.title,
        textSpec.deleteModal.errorMessage.message,
      )
    },
    )
  }
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="ml-auto" onClick={(e) => e.preventDefault()}>
      <Dropdown horizontal>
        <Dropdown.Item onClick={() => setIsUpdateModalOpen(true)}>
          {textSpec.updateModal.button.text}
        </Dropdown.Item>
        <Dropdown.Item onClick={() => setIsDuplicateOpen(true)}>
          {textSpec.duplicateModal.button.text}
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item onClick={() => {
          if (project.has_designs) {
            ToastHelper.error('Cannot delete project', 'You cannot delete a project that has designs associated with it')
          } else {
            setIsDeleteModalOpen(true)
          }
        }}
        >
          {textSpec.deleteModal.button.text}
        </Dropdown.Item>
      </Dropdown>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={onDelete}
        title={textSpec.deleteModal.modal.title}
        isLoading={isLoading}
        message={textSpec.deleteModal.modal.message}
      />
      <UpdateProjectModal
        project={project}
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onSuccess={(project) => navigate(`/projects/${project.id}`)}
      />
      <DuplicateProjectModal
        project={project}
        isOpen={isDuplicateOpen}
        onClose={() => setIsDuplicateOpen(false)}
        onSuccess={() => {
          setIsDuplicateOpen(false)
        }}
      />
    </div>
  )
}

function BrowseProjectsView ({ projects, footerElements, viewType = 'list', isLoading = false }: PropTypes) {
  return (
    <div className="w-full">
      {
        viewType === 'list'
          ? (
            <ProjectListView
              projects={projects}
              footerElements={footerElements}
              dropdown={ProjectDropdown}
            />)
          : (
            <ProjectGridView
              projects={projects}
              footerElements={footerElements}
              dropdown={ProjectDropdown}
            />)
      }
      {
        isLoading
          ? <div className="w-full flex items-center justify-center pt-2">
            <LoadingSpinner className="h-4 w-4" />
          </div>
          : null
      }
    </div>
  )
}

export default BrowseProjectsView
